import _ from 'lodash';
import type { FixMeLater } from '../../../types/fix-me-later-type';

/**
 * Period total for one side of an ADR calculation.
 *
 * The overview query fetches revenue and sold for this year, last year and last year
 * final, but for the N-years-ago comparison it fetches only the variances against
 * those totals, not the totals themselves. When the direct field is missing the total
 * is recovered from the variance, which is defined as base - comparison
 * (var_revenue_ly_n = revenue - revenue_ly_n).
 * @param rows The daily rows making up one aggregated row.
 * @param baseKey The current year field. Either 'revenue' or 'sold'.
 * @param metricKey The field being totalled. Eg. 'revenue', 'sold_ly', 'revenue_ly_n'.
 * @returns The summed total for the period.
 */
const periodTotal = (rows: FixMeLater[], baseKey: string, metricKey: string): number => {
  if (metricKey !== baseKey && !rows.some((row) => row?.[metricKey] != null)) {
    return _.sumBy(rows, baseKey) - _.sumBy(rows, `var_${metricKey}`);
  }
  return _.sumBy(rows, metricKey);
};

/**
 * ADR for a group of daily rows, rebuilt from the period totals.
 * @param rows The daily rows making up one aggregated (week/month/quarter/year) row.
 * @param adrKey The ADR field being aggregated. Eg. 'adr', 'adr_ly', 'adr_ly_final_n'.
 * @returns Revenue divided by rooms sold, or null when there are no room nights to
 * divide by, so the cell renders blank rather than $0.00.
 */
export const periodAdr = (rows: FixMeLater[], adrKey: string): number | null => {
  const revenue = periodTotal(rows, 'revenue', adrKey.replace('adr', 'revenue'));
  const sold = periodTotal(rows, 'sold', adrKey.replace('adr', 'sold'));
  return sold > 0 ? revenue / sold : null;
};

/**
 * ADR variance for an aggregated (week/month/quarter/year) row.
 *
 * ADR is a ratio, so the variance has to be rebuilt from the period totals the same
 * way the ADR columns themselves are. Averaging the per-day variances instead weights
 * a 20 room night day the same as a 150 room night day, and lets days with no
 * comparable data contribute a full ADR as a false positive.
 *
 * Mirrors the server side getVariance applied to daily rows: var_adr_ly -> adr - adr_ly.
 * Both ADRs are rounded to cents first so the result always equals the difference of
 * the ADR and ADR LY cells as they are displayed on the same row.
 * @param rows The daily rows making up one aggregated row.
 * @param varKey The variance field being aggregated. Eg. 'var_adr_ly', 'var_adr_ly_n'.
 * @returns The variance, or null when either side has no room nights.
 */
export const periodAdrVariance = (rows: FixMeLater[], varKey: string): number | null => {
  const adr = periodAdr(rows, 'adr');
  const adrCompare = periodAdr(rows, varKey.replace('var_', ''));

  if (adr === null || adrCompare === null) {
    return null;
  }

  return _.round(_.round(adr, 2) - _.round(adrCompare, 2), 2);
};
